"use client";

import Link from "next/link";
import { ArrowRight, Server, MapPin, Languages } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { globalOps as data } from "@/lib/data/about";

const iconMap = { Server, MapPin, Languages } as const;
const pillarAccents = [
  "var(--color-cyan)",
  "var(--color-purple)",
  "var(--color-amber)",
] as const;

export function GlobalOps() {
  return (
    <Section id="global-ops" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute bottom-0 right-1/3 h-96 w-96 rounded-full bg-[var(--color-cyan)] opacity-20 blur-[140px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-3 gap-5">
          {data.pillars.map((p, i) => {
            const Icon = (iconMap[Object.keys(iconMap)[i % Object.keys(iconMap).length] as keyof typeof iconMap] ?? Server);
            const accent = pillarAccents[i % pillarAccents.length]!;
            return (
              <Reveal key={p.title} delay={i * 100}>
                <div
                  className="glass rounded-2xl p-6 h-full relative overflow-hidden"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className="grid place-items-center h-11 w-11 rounded-xl"
                      style={{
                        background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-5 w-5" style={{ color: accent }} />
                    </span>
                    <span className="eyebrow-tiny font-bold" style={{ color: accent }}>
                      {p.metric}
                    </span>
                  </div>
                  <h3 className="text-lg">{p.title}</h3>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={400}>
          <div className="mt-10 glass rounded-2xl p-6">
            <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">By the Numbers</div>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {data.byTheNumbers.map((n) => (
                <div key={n.label} className="text-center sm:text-left">
                  <div className="text-3xl font-bold text-[var(--color-cyan)]">{n.value}</div>
                  <div className="text-xs text-[rgb(var(--fg-muted))] mt-1 leading-snug">{n.label}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
